import { Avatar, Divider, Grid, Stack, Typography } from '@mui/material';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useDictionary, useIsDesktop } from 'Hooks';
import { differenceInDays, format } from 'date-fns';
import { Status } from '../../Status';
import { Equipment_Order_STATUSES, IEquipmentPrevOrder } from 'API';
import { useNavigate } from 'react-router-dom';
import { OneOrTwoButtons } from 'Components/OneOrTwoButtons';
import { shortDateFormat, shorterDateFormat } from 'Config';
import { Copy, PurpleOutlineWarningIcon } from 'Images';

interface IOrderCard {
  item: IEquipmentPrevOrder;
  onShowOrderDetailsClick: (clickedOrder: any) => void;
  titles: string[];
}

export const OrderCard = ({ item, titles }: IOrderCard) => {
  const dictionary = useDictionary(Dictionary, DICTIONARY.Equipment);
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();

  const daysDiff = differenceInDays(
    new Date(item.endDate),
    new Date(item.startDate)
  );

  const monthsDiff = Math.floor(daysDiff / 30);
  const orderID = item.requestNumber || item.internetRequestNumber;

  const handleNavigate = () => {
    navigate(`/Equipment/${orderID}`);
  };

  return (
    <Grid
      component={'li'}
      container
      sx={{
        border: '1px solid',
        borderColor: 'purple2.shade_200',
        p: '24px',
        bgcolor: '#fff',
        borderRadius: '16px',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <Stack>
        <Stack direction='row' alignItems='center'>
          <Grid item xs={12} md={1.5} sx={{ cursor: 'pointer' }}>
            <Stack>
              <Typography
                variant='body6'
                sx={{ mr: isDesktop ? undefined : '6px' }}
              >
                {titles[0]}
              </Typography>
              <Typography variant='body5'>
                {item.startDate &&
                  format(new Date(item.startDate), shortDateFormat)}
              </Typography>
            </Stack>
          </Grid>
          <Grid item xs={6} sm={2} sx={{ cursor: 'pointer' }}>
            <Stack>
              <Typography
                variant='body6'
                sx={{ mr: isDesktop ? undefined : '6px' }}
              >
                {titles[1]}
              </Typography>
              <Typography variant='body2'>
                {item.requestNumber
                  ? item.requestNumber
                  : dictionary.orderDialog.willBeSettledLater}
              </Typography>
            </Stack>
          </Grid>
          <Grid item xs={6} sm={1.5} sx={{ cursor: 'pointer' }}>
            <Stack>
              <Typography
                variant='body6'
                sx={{ mr: isDesktop ? undefined : '6px' }}
              >
                {titles[2]}
              </Typography>
              {item.orderItemsCount && (
                <Typography variant='body2'>{item.orderItemsCount}</Typography>
              )}
            </Stack>
          </Grid>
          <Grid item xs={12} md={3} sx={{ cursor: 'pointer' }}>
            <Stack
              sx={
                isDesktop
                  ? { m: '0 32px' }
                  : { flexDirection: 'row', m: '12px 0', alignItems: 'center' }
              }
            >
              <Typography
                variant='body6'
                sx={{ mr: isDesktop ? undefined : '6px' }}
              >
                {titles[3]}
              </Typography>
              <Status
                status={item.currentStatus}
                statusLabel={item.currentStatusLabel}
              />
            </Stack>
          </Grid>
          <Grid item xs={12} md={4}>
            <OneOrTwoButtons
              width='max-content'
              leftButton={{
                label: dictionary.RecentOrders.moreInfo,
                onClick: handleNavigate,
                ariaLabel: `${dictionary.RecentOrders.moreInfo} ${
                  dictionary.RecentOrders.aboutOrderFrom
                } ${format(new Date(item.createDate), shortDateFormat)}`,
              }}
              leftButtonRole='link'
              rightButton={
                item.currentStatus > Equipment_Order_STATUSES.takenCareByDept4
                  ? {
                      label: dictionary.RecentOrders.duplicateOrder,
                      onClick: () =>
                        navigate(`/Equipment/createOrder/${orderID}`),
                      icon: Copy,
                      ariaLabel: `${dictionary.RecentOrders.duplicateOrder} ${item.requestNumber}`,
                    }
                  : undefined
              }
              rightButtonRole='link'
            />
          </Grid>
        </Stack>

        {!!item.adminIsrId && (
          <Stack
            alignItems={'center'}
            gap={'6px'}
            flexDirection={'row'}
            mt='20px'
          >
            <Avatar
              sx={{ width: '20px', height: '20px' }}
              alt=''
              src={PurpleOutlineWarningIcon}
            />
            <Typography variant='body3' role='note'>
              {Dictionary[DICTIONARY.Applies].openedByWing}
            </Typography>
          </Stack>
        )}

        {monthsDiff > 0 && (
          <>
            <Divider
              aria-hidden='true'
              flexItem
              sx={{
                margin: '20px 0',
                borderColor: 'primary.shade_100',
              }}
            />
            <Stack flexDirection='row' alignItems='center'>
              <Avatar
                src={PurpleOutlineWarningIcon}
                sx={{ width: '20px', height: '20px', mr: 1 }}
              />
              <Typography
                variant='body6'
                sx={{ mr: isDesktop ? undefined : '6px' }}
                role='note'
              >
                {dictionary.RecentOrders.orderPeriodInfo(
                  monthsDiff,
                  format(new Date(item.endDate), shorterDateFormat)
                )}
              </Typography>
            </Stack>
          </>
        )}
      </Stack>
    </Grid>
  );
};
